import React from 'react';
import { motion } from 'framer-motion';
import { reveal } from '../utils/motion';
import { CaseStudyCard } from '../components/CaseStudyCard';
import { SiteHeader } from '../components/SiteHeader';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { DrawnUnderline } from '../components/DrawnUnderline';
import { caseStudies, heroMetrics } from '../data/caseStudies';
import { clientLogos } from '../data/clientLogos';
import { linkClass } from '../utils/linkClass';

// Dark grey → black, applied per line so it stays consistent across the underlined word.
const gradientText = 'bg-[linear-gradient(to_bottom,#4a4a4a,#000000)] bg-clip-text text-transparent [-webkit-box-decoration-break:clone] [box-decoration-break:clone]';
export function Home() {
  return <div className="relative min-h-screen w-full bg-white font-sans text-ink" data-id="element-142" data-mp="3kvtmbh3q5vz0">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[760px] min-[700px]:h-[900px]" style={{
      backgroundImage: 'radial-gradient(rgba(0, 0, 0, 0.14) 1px, transparent 1.5px)',
      backgroundSize: '32px 32px',
      maskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%)'
    }} data-id="element-143" data-mp="3owvlxz1k0a1i" />
      <div className="relative mx-auto flex max-w-[1347px] flex-col gap-[30px] px-5 py-10 md:px-10" data-id="element-144" data-mp="2mwqq76jix02p">
        <SiteHeader data-id="element-145" data-mp="2hz8fpbpuycsl" />

        <main className="flex flex-col gap-5 min-[700px]:gap-[30px]" data-id="element-146" data-mp="2hzqg8kjjzd6b">
          <section className="grid grid-cols-1 gap-8 py-4 min-[700px]:py-8 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] md:gap-10 md:py-12 xl:gap-16" data-id="element-147" data-mp="2d5w1zwmpvqem">
            <motion.div className="flex flex-col justify-center gap-4" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={reveal} data-id="element-148" data-mp="239j8ozv33eke">
              <h1 className="text-headline font-semibold text-black lg:text-[3.5rem] lg:leading-[1.1]" data-id="element-149" data-mp="1u82z2uc3fgh8">
                <span className={gradientText} data-id="element-150" data-mp="1kjnwp8n4mdz3">
                  18 years of experience across B2B SaaS, fintech, and healthcare.
                </span>
              </h1>
              <p className="text-subhead text-muted" data-id="element-151" data-mp="1amt0iwebspnz">
                Specialize in driving business results through{' '}
                <DrawnUnderline delay={2} data-id="element-152" data-mp="1fmp4ivn0wh3j">improved usability</DrawnUnderline>.
              </p>
            </motion.div>

            <ul aria-label="Results" className="flex flex-col justify-center gap-4 border-t border-black/10 pt-6 md:gap-6 md:border-l md:border-t-0 md:pl-8 md:pt-0 xl:pl-12" data-id="element-153" data-mp="23bqieyemkkg7">
              {heroMetrics.map((m, i) => <motion.li key={m.label} className="flex items-start gap-3" initial={{
              opacity: 0,
              y: 24
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              ...reveal,
              delay: 0.15 + i * 0.06
            }} data-id="element-154" data-mp="1yjynkbofrsk9" data-mpi={i}>
                  {/* Caption is half the number's size at the same line height, so its two lines match the number's height. */}
                  <span className="min-w-[3.2em] shrink-0 whitespace-nowrap text-right text-[3rem] font-normal leading-[1.35] tracking-[-0.02em] text-positive" data-id="element-155" data-mp="alxwp0kbo70o">
                    {m.value}
                  </span>
                  <span className="whitespace-pre-line text-[1.5rem] leading-[1.35] text-muted" data-id="element-156" data-mp="kdc3zuiimb4d">
                    {m.label}
                  </span>
                </motion.li>)}
            </ul>
          </section>

          <motion.section aria-label="Clients" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          ...reveal,
          delay: 0.35
        }} data-id="element-157" data-mp="5nmn8qxxvyiz">
            <ul className="grid grid-cols-3 items-center gap-x-4 gap-y-0 min-[700px]:gap-y-2 md:grid-cols-6" data-id="element-158" data-mp="1h2t5ryednd0s">
              {clientLogos.map(logo => <li key={logo.name} className="flex items-center justify-center" data-id="element-159" data-mp="1c2x4i6x2dzox">
                  <img src={logo.src} alt={logo.name} className="block aspect-[2/1] w-full max-w-[200px] object-contain" data-id="element-160" data-mp="27ey9hvtm7xy2" />
                </li>)}
            </ul>
          </motion.section>

          <section aria-label="Case studies" className="flex flex-col gap-[30px]" data-id="element-161" data-mp="2hb184rp9deg9">
            {caseStudies.map(study => <CaseStudyCard key={study.id} study={study} animate data-id="element-162" data-mp="2mcgwotqna33e" />)}
          </section>

          <section className="flex flex-col gap-[30px] pt-2" data-id="element-163" data-mp="2qchacc5x6a9e">
            <div className="flex flex-col gap-3" data-id="element-164" data-mp="2vashug8qs8og">
              <h2 className="text-headline font-semibold" data-id="element-165" data-mp="2v9heghj7l2xv">
                AI has become the core of my design practice.
              </h2>
              <p className="text-subhead text-muted" data-id="element-166" data-mp="351wydke2g1le">
                I prototype in code and can ship working software.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-[30px] md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]" data-id="element-167" data-mp="39yx2vqv9owae">
              <div className="flex flex-col gap-10" data-id="element-168" data-mp="3dzz3ab0x7edy">
              <div className="flex flex-col gap-4 text-base leading-[1.6]" data-id="element-169" data-mp="12g19cdd6djhc">
                <p data-id="element-170" data-mp="ycv7rj10cgh7">
                  My primary design tool is Magic Patterns, where I maintain working prototypes of client projects. 
                </p>
                <a href="https://project-unified-inbox-with-filtering-117.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className={`w-fit text-base ${linkClass}`} data-id="element-171" data-mp="tge9hw03glqe">
                  View RSI Prototype →
                </a>
              </div>
              <div className="flex flex-col gap-4 text-base leading-[1.6]" data-id="element-172" data-mp="1cce4lfodc5e7">
                <p data-id="element-173" data-mp="17e2xvdtdeb07">
                  I built Percelo, an AEO (Answer Engine Optimization), tool solo in about two weeks with Claude Code. 
                </p>
                <a href="https://www.percelo.com" target="_blank" rel="noopener noreferrer" className={`w-fit text-base ${linkClass}`} data-id="element-174" data-mp="24urt2ontef0m">
                  Try Percelo →
                </a>
              </div>
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/8x9mJt8jkvyUyDgp9GG3CK/Percelo-hero2.png" alt="Percelo AI visibility report open on a laptop" className="h-[325px] w-full self-start rounded-[30px] object-cover object-[50%_45%]" data-id="element-175" data-mp="28wbg2p96z6l" />
            </div>
          </section>

          <TestimonialsSection data-id="element-176" data-mp="3fly9qh0tmcod" />
        </main>

        <div className="pt-10" data-id="element-177" data-mp="3fjlhnb1cmlxl">
          <SiteHeader as="footer" data-id="element-178" data-mp="31m6phyjpo01x" />
        </div>
      </div>
    </div>;
}